<template>
  <div class="filter filter-date">{{ $t('Дата') }}<div class="d-flex ga-4">
      <DatePicker
        :placeholder="$t('С')"
        v-model:value="searchParams.createdAt.dates[0]"
        :lang="lang"
      />
      <DatePicker
        :placeholder="$t('По')"
        v-model:value="searchParams.createdAt.dates[1]"
        :lang="lang"
      />
    </div>
  </div>
  <div class="filter filter-madeBy">{{ $t('Пользователь-субъект') }}<Input
      placeholder="Поиск по логину пользователя"
      v-model="searchParams.madeByUsername"
      hide-details
      clear-icon="mdi-close"
      clearable
    />
  </div>
  <div class="filter filter-event-type">{{ $t('Действие') }}<v-combobox
      :placeholder="$t('Тип запроса')"
      v-model="searchParams.selectedEventIds"
      :disabled="!props.eventList.length"
      :items="props.eventList"
      :return-object="false"
      item-title="name"
      item-value="id"
      multiple
      variant="solo"
      hide-details
      clear-icon="mdi-close"
      clearable
      @update:model-value="onUpdateSelectedEventIds"
    >
      <template #selection="{ index }">
        <span v-if="index === 0">
          {{ $t('Выбрано:') }} {{ searchParams.selectedEventIds.length }}
        </span>
      </template>
    </v-combobox>
  </div>
  <div class='filter filter-status'>{{ $t('Статус') }}<v-select
      :placeholder="$t('Результат')"
      v-model="searchParams.isSuccess"
      :items="[
        { title: $t('Успешно'), value: true },
        { title: $t('Неудачно'), value: false },
      ]"
      variant="solo"
      hide-details
      clear-icon="mdi-close"
      clearable
    />
  </div>
  <div class='filter filter-event-id'>{{ $t('ID события') }}<Input
      placeholder="ID"
      v-model="searchParams.id"
      type="number"
      hide-details
      clear-icon="mdi-close"
      clearable
    />
  </div>
  <div class="align-content-end">
    <ExtendedSearch
      :table-model="tableModel"
      :search-params="searchParams"
      @search="searchFn"
      @reset-search="resetFormFn"
    />
  </div>
</template>

<script setup lang="ts">
import { reactive } from "vue";
import { formatDateTime } from "@/filters";
import DatePicker from 'vue-datepicker-next';
import Input from "@intec/vue-components/form/Input.vue";
import ExtendedSearch from "@intec/vue-components/search/ExtendedSearch.vue";
import Config from '@/config';

const props = defineProps({
  tableModel: {
    type: Object,
    required: true
  },
  eventList: {
    type: Object,
    required: true
  },
});
const lang = Config.getUser().getLang();

const searchParams = reactive({
  createdAt: {
    dates: [],
  },
  madeByUsername: null,
  selectedEventIds: [],
  isSuccess: null,
  id: null,
});

const onUpdateSelectedEventIds = (newValue) => {
  if (Array.isArray(newValue)) {
    searchParams.selectedEventIds = newValue.filter(id => {
      return props.eventList.some(event => event.id === id);
    });
  }
};

const searchFn = () => {
  const apiParams = {};

  if (searchParams.createdAt.dates[0]) {
    apiParams['createdAt[after]'] = formatDateTime(searchParams.createdAt.dates[0]);
  }
  
  if (searchParams.createdAt.dates[1]) {
    const beforeDate = new Date(searchParams.createdAt.dates[1]);
    beforeDate.setHours(23, 59, 59, 999);
    apiParams['createdAt[before]'] = formatDateTime(beforeDate.toISOString());
  }

  if (searchParams.madeByUsername) {
    apiParams['madeBy.username'] = searchParams.madeByUsername;
  }

  if (searchParams.selectedEventIds) {
    apiParams['event.id'] = searchParams.selectedEventIds;
  }

  if (searchParams.isSuccess !== null) {
    apiParams['success'] = searchParams.isSuccess;
  }

  if (searchParams.id) {
    apiParams['id'] = searchParams.id;
  }

  props.tableModel.bind.options.page = 1;
  props.tableModel.reload({ searchParams: apiParams });
};

const resetFormFn = () => {
  searchParams.createdAt.dates = [];
  searchParams.madeByUsername = null;
  searchParams.selectedEventIds = [];
  searchParams.isSuccess = null;
  searchParams.id = null;
  props.tableModel.bind.options.page = 1;
  props.tableModel.reload({ searchParams: searchParams });
};
</script>

<style lang="scss" scoped>
.filter {
  min-width: 150px;
  max-width: 435px;
  display: flex;
  flex-direction: column;
  gap: 15px;
  font-family: Abrade !important;
  font-weight: 500 !important;

  &-madeBy {
    width: 250px;
  }

  &-event-type {
    width: 320px;
  }

  &-status {
    width: 175px;
  }
  
  &-madeBy, &-event-id {
    :deep(.v-field--variant-solo) {
      border: none;
    }
  }
}

:deep(.mdi-close) {
  font-size: 16px;
  opacity: 1;
  color: rgba(var(--black-color), 0.5);
}

:deep(.mdi-close:hover) {
  color: rgba(var(--black-color), 1);
}

:deep(.v-field) {
  box-shadow: none;
  padding: 0;
}

:deep(.mx-input), :deep(.v-input) {
  height: 40px;
  max-height: 40px;
  background-color: rgb(var(--white-color));
  border: 1px solid rgb(var(--grey-border-color));
  border-radius: 8px;
  box-shadow: none;
  align-content: center;
  &::placeholder {
    color: rgba(var(--black-color), 0.4);
    opacity: unset;
    font-size: 12px;
  };
}

:deep(.v-field__input) {
  height: 38px;
  min-height: auto;
  padding: 6px 11px;
  &::placeholder {
    color: rgba(var(--black-color), 0.4);
    opacity: unset;
    font-size: 12px !important;
    font-weight: 500 !important;
  };
}

:deep(.v-field__input input) {
  &::placeholder {
    color: rgba(var(--black-color), 0.5);
    opacity: unset;
    font-size: 12px !important;
    font-weight: 500 !important;
  };
}

:deep(.v-combobox__content) {
  width: min-content !important;
}
</style>
